import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { getMaterialRequest } from "../services/MaterialRequestService";
import type { MaterialRequest } from "../types/material-request.types";
import MaterialRequestOverviewContext from "./MaterialRequestOverviewContext";
import Section from "./Section";
import { materialRequestDisplayCode } from "../utils/material-request.utils";

const MaterialRequestOverview = () => {
  const { id } = useParams();
  const [materialRequest, setMaterialRequest] =
    useState<MaterialRequest | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!id) return;
    const numericId = Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    try {
      const res = await getMaterialRequest(numericId);
      if (res.success && res.data) {
        setMaterialRequest(res.data);
      } else {
        setMaterialRequest(null);
      }
    } catch {
      setMaterialRequest(null);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      await refetch();
      if (!cancelled) setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id, refetch]);

  document.title = getDocumentTitle(
    materialRequest
      ? materialRequestDisplayCode(materialRequest)
      : "Material request",
  );

  if (loading) {
    return <PageLoadingSpinner message="Loading material request…" />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Material request"
          pageTitle="Inventory Management"
          items={[
            {
              label: "Material requests",
              path: "/inventory-management/material-requests",
            },
          ]}
        />
        <MaterialRequestOverviewContext.Provider
          value={{ materialRequest, refetch }}
        >
          <Section />
        </MaterialRequestOverviewContext.Provider>
      </Container>
    </div>
  );
};

export default MaterialRequestOverview;
